import { __rest } from '../../node_modules/tslib/tslib.es6.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import React__default, { forwardRef, useRef, useId, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import cns from '../../node_modules/classnames/index.js';
import DeleteIcon from '../Icons/DeleteIcon.js';
import Spinner from '../Spinner/Spinner.js';
import DontShowAgain from '../DontShowAgain/DontShowAgain.js';
import useChangeModalPosition from './useChangeModalPosition.js';
import usePortalElement from './usePortalElement.js';
import useBodyModalOpenClass from './useBodyModalOpenClass.js';
import Body from './Body.js';
import Header from './Header.js';
import Footer from './Footer.js';
import useModalContext from '../ModalContext/useModalContext.js';
import { sameType } from '../../utils/react.js';
import { NOOP } from '../../utils/const.js';
import useDontShowAgainMessageRead from '../DontShowAgain/useDontShowAgainMessageRead.js';

function ModalCloseButton({ onClose = undefined }) {
    return (jsx("div", { className: "modal__close", onClick: onClose, role: "presentation", children: jsx(DeleteIcon, {}) }));
}
const ModalContent = forwardRef((props, ref) => {
    const { children, className, x, y, style } = props, others = __rest(props, ["children", "className", "x", "y", "style"]);
    return (jsx("div", Object.assign({ className: className, style: Object.assign({ marginLeft: x, marginTop: y }, style), ref: ref, tabIndex: -1 }, others, { children: children })));
});
ModalContent.defaultProps = {
    style: {},
};
/**
Presenta una finestra modale con Body, Header e Footer.
Crea un elemento a livello body su cui renderizzare la modale.
 */
function Modal(_a) {
    var { children = null, className = '', contentClassName = '', doNotFocus = false, initialPos = {
        x: 'auto',
        y: (window.innerHeight * 0.05),
    }, loading = false, onClose = NOOP, show = false, style = {}, dontShowAgainId = undefined } = _a, others = __rest(_a, ["children", "className", "contentClassName", "doNotFocus", "initialPos", "loading", "onClose", "show", "style", "dontShowAgainId"]);
    const contentRef = useRef(null);
    const modalId = useId();
    const portalElementRef = usePortalElement(show, modalId);
    const { x, y, overflow, maxHeight, dragging, onMouseDown, } = useChangeModalPosition(initialPos, contentRef, portalElementRef);
    useBodyModalOpenClass(show);
    useModalContext(show);
    const messageRead = useDontShowAgainMessageRead(dontShowAgainId, show);
    useEffect(() => {
        if (!doNotFocus && contentRef.current)
            contentRef.current.focus();
    }, [doNotFocus]);
    const modalCloseButtonElement = useMemo(() => jsx(ModalCloseButton, { onClose: onClose }), [onClose]);
    const childrenWithProps = useMemo(() => {
        const newChildren = [];
        React__default.Children.forEach(children, (child) => {
            if (sameType(child, Body)) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                newChildren.push(React__default.cloneElement(child, {
                    overflow,
                    maxHeight,
                    key: newChildren.length + 1,
                }));
                if (dontShowAgainId) {
                    newChildren.push(jsx(DontShowAgain, { className: "modal-dontshowagain", id: dontShowAgainId }));
                }
            }
            else if (sameType(child, Header)) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                newChildren.push(React__default.cloneElement(child, {
                    dragging,
                    onMouseDown,
                    key: newChildren.length + 1,
                }));
            }
            else {
                newChildren.push(child);
            }
        });
        return newChildren;
    }, [children, overflow, maxHeight, dontShowAgainId, dragging, onMouseDown]);
    if (!show)
        return null;
    if (messageRead) {
        onClose();
        return null;
    }
    const classes = cns('modal', className);
    const contentClasses = cns('modal--content', contentClassName);
    if (!portalElementRef.current)
        return null;
    return createPortal((jsxs("div", { className: classes, children: [jsx("div", { className: "modal--overlay" }), jsx("div", { className: "modal--dialog", children: jsxs(ModalContent, Object.assign({ className: contentClasses, x: x, y: y, style: style, ref: contentRef }, others, { children: [modalCloseButtonElement, childrenWithProps, loading && jsx("div", { className: "modal__loader", children: jsx(Spinner, { large: true }) })] })) })] })), portalElementRef.current);
}
Modal.Body = Body;
Modal.Header = Header;
Modal.Footer = Footer;
Modal.clearMessageStatus = DontShowAgain.clearMessageStatus;

export { Modal as default };
